import { cn } from '@/lib/utils';
import type { LayoutFamily, CustomTemplateSettings } from '@/types/announcement';
import { DEFAULT_DECORATIONS, DEFAULT_SECTIONS } from '@/types/announcement';

interface LayoutFamilyPickerProps {
  selected: LayoutFamily;
  onSelect: (family: LayoutFamily, defaults: Partial<CustomTemplateSettings>) => void;
}

interface LayoutOption {
  id: LayoutFamily;
  label: string;
  emoji: string;
  desc: string;
  defaults: Partial<CustomTemplateSettings>;
}

const LAYOUT_OPTIONS: LayoutOption[] = [
  {
    id: 'magazine_cover', label: 'Magazine Cover', emoji: '📰', desc: 'Bold hero photo with title overlay',
    defaults: { headlineFont: 'bold_editorial', bodyFont: 'clean_sans', backgroundStyle: 'dark_dramatic', photoLayout: 'hero', photoShape: 'rectangle', fontColor: '#ffffff', backgroundColor: '#1a1a2e', emphasisStyle: 'oversized_title', sections: { ...DEFAULT_SECTIONS, subtitle: true } },
  },
  {
    id: 'editorial_story', label: 'Editorial Story', emoji: '📖', desc: 'Headlines, pull quotes & story blocks',
    defaults: { headlineFont: 'elegant_serif', bodyFont: 'clean_serif', backgroundStyle: 'textured_paper', photoLayout: 'editorial_framed', photoShape: 'rectangle', fontColor: '#1a1a2e', backgroundColor: '#f4ead5', emphasisStyle: 'italic_pullquote', sections: { ...DEFAULT_SECTIONS, pullQuote: true, subtitle: true } },
  },
  {
    id: 'love_letter', label: 'Love Letter', emoji: '💌', desc: 'Handwritten romantic feel',
    defaults: { headlineFont: 'handwritten_script', bodyFont: 'clean_serif', backgroundStyle: 'romantic_warm', photoLayout: 'side_by_side_circles', photoShape: 'circle', fontColor: '#4a1942', backgroundColor: '#fff5f5', emphasisStyle: 'none', decorations: { ...DEFAULT_DECORATIONS, glow: true, hearts: true } },
  },
  {
    id: 'scrapbook', label: 'Scrapbook', emoji: '📸', desc: 'Tilted photos with tape accents',
    defaults: { headlineFont: 'modern_sans', bodyFont: 'clean_serif', backgroundStyle: 'textured_paper', photoLayout: 'tilted_scrapbook', photoShape: 'polaroid', fontColor: '#3d0c02', backgroundColor: '#f5f0e8', decorations: { ...DEFAULT_DECORATIONS, tape: true }, sections: { ...DEFAULT_SECTIONS, stickerRow: true } },
  },
  {
    id: 'minimal_modern', label: 'Minimal Modern', emoji: '◻️', desc: 'Clean lines, generous whitespace',
    defaults: { headlineFont: 'modern_sans', bodyFont: 'clean_sans', backgroundStyle: 'solid', photoLayout: 'hero', photoShape: 'rounded', fontColor: '#1a1a2e', backgroundColor: '#ffffff', emphasisStyle: 'none' },
  },
  {
    id: 'polaroid_collage', label: 'Polaroid Collage', emoji: '🎞️', desc: 'Scattered photo memories',
    defaults: { headlineFont: 'elegant_serif', bodyFont: 'clean_serif', backgroundStyle: 'vignette', photoLayout: 'tilted_scrapbook', photoShape: 'polaroid', fontColor: '#1a1a2e', backgroundColor: '#fafaf9', sections: { ...DEFAULT_SECTIONS, photoCaption: true } },
  },
  {
    id: 'bold_poster', label: 'Bold Poster', emoji: '🎯', desc: 'Big type, vibrant energy',
    defaults: { headlineFont: 'bold_editorial', bodyFont: 'clean_sans', backgroundStyle: 'gradient', photoLayout: 'side_by_side_circles', photoShape: 'circle', fontColor: '#ffffff', backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', emphasisStyle: 'oversized_title', sections: { ...DEFAULT_SECTIONS, subtitle: true, stickerRow: true } },
  },
  {
    id: 'soft_romantic', label: 'Soft Romantic', emoji: '🌸', desc: 'Soft gradients, floral warmth',
    defaults: { headlineFont: 'elegant_serif', bodyFont: 'clean_serif', backgroundStyle: 'romantic_warm', photoLayout: 'floating_cluster', photoShape: 'rounded', fontColor: '#4a1942', backgroundColor: '#fce7f3', decorations: { ...DEFAULT_DECORATIONS, florals: true, glow: true } },
  },
  {
    id: 'memory_board', label: 'Memory Board', emoji: '📋', desc: 'Photo grid with note cards',
    defaults: { headlineFont: 'modern_sans', bodyFont: 'clean_sans', backgroundStyle: 'solid', photoLayout: 'collage_grid', photoShape: 'rounded', fontColor: '#1a1a2e', backgroundColor: '#f5f5f5', sections: { ...DEFAULT_SECTIONS, date: true } },
  },
  {
    id: 'elegant_classic', label: 'Elegant Classic', emoji: '✨', desc: 'Traditional letter with ornaments',
    defaults: { headlineFont: 'elegant_serif', bodyFont: 'clean_serif', backgroundStyle: 'solid', photoLayout: 'side_by_side_circles', photoShape: 'rounded', fontColor: '#1a1a2e', backgroundColor: '#fdf6e3', decorations: { ...DEFAULT_DECORATIONS, flourishes: true } },
  },
];

export function LayoutFamilyPicker({ selected, onSelect }: LayoutFamilyPickerProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-foreground">Choose your layout style</p>
      <div className="grid grid-cols-2 gap-2">
        {LAYOUT_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onSelect(opt.id, opt.defaults)}
            className={cn(
              'flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-all active:scale-[0.97]',
              selected === opt.id
                ? 'border-primary bg-primary/5 shadow-sm'
                : 'border-border hover:border-primary/30'
            )}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{opt.emoji}</span>
              <span className={cn('text-sm font-medium', selected === opt.id ? 'text-foreground' : 'text-muted-foreground')}>
                {opt.label}
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground leading-tight">{opt.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
